import { useDictionary } from "@/ProtectedRoutes/ProtectedRoutes";
import { toast } from "react-toastify";
import Cookies from "js-cookie";


export const getServicesFromCookie = () => {
  const data = Cookies.get("servicesDispatchDotList");
  return data ? JSON.parse(data) : null;
};

export const setServicesToCookie = (data: any) => {
  Cookies.set("servicesDispatchDotList", JSON.stringify(data), { expires: 1 }); // 1 day
};

export const useRetrieveTitle = () => {
  const {
    languageObj,
    direction,
    language,
    langOption,
    defaultLanguage,
    handleLanguageChange,
    isPending,
  } = useDictionary();

  const getTitle = (key: string): string => {
    return languageObj?.[key] || "";
  };

  const getMultipleTitles = (...keys: string[]): string => {
    return keys.map((k) => languageObj?.[k] || "").join(" ");
  };

  const isRTL = direction === "rtl";

  const showError = (key: string) => {
    toast.error(getTitle(key));
  };

  const showSuccess = (key: string) => {
    toast.success(getTitle(key));
  };


  return {
    getTitle,
    getMultipleTitles,
    showError,
    showSuccess,
    languageObj,
    direction,
    isRTL, // ✅ merged here
    language,
    langOption,
    defaultLanguage,
    handleLanguageChange,
    isPending,
  };
};

export const formatColorCode = (colorCode: any) => {
  if (!colorCode) {
    return "#000000";
  }
  const regex = /^#?([a-f\d]{2})([a-f\d]{6})$/i;
  const match = colorCode?.match(regex);

  if (match) {
    const alpha = match[1]?.toUpperCase();
    const remainingHex = match[2];
    return `#${remainingHex}${alpha}`;
  } else {
    return colorCode;
  }
};



export const getInitials = (name: string) => {
  if (!name) return "";
  const names = name.split(" ");
  const initials = names.map((name) => name.charAt(0).toUpperCase());
  return initials.join("");
};

export const getTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export function hexToRgba(hex: string) {
  hex = hex.replace("#", "");
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  let rgbaColor = "rgba(" + r + "," + g + "," + b + ",0.1)";

  let rgbaArray = rgbaColor.substring(5, rgbaColor.length - 1).split(",");
  let rgbaHex = rgbaArray
    .map(function (value, index) {
      if (index === 3) {
        let opacityHex = Math.round(parseFloat(value) * 255).toString(16);
        return opacityHex.length === 1 ? "0" + opacityHex : opacityHex;
      }
      let componentHex = parseInt(value).toString(16);
      return componentHex.length === 1 ? "0" + componentHex : componentHex;
    })
    .join("");

  return "#" + rgbaHex.toUpperCase();
}

export const capitalizeFirstLetter = (inputString: string) => {
  return (
    inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase()
  );
};

export const useDownloadJson = (data: any, filename: string) => {
  const jsonData = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonData], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const setThemeStyles = (theme: any) => {
  const css = document.documentElement.style;
  Object.entries(theme).forEach(([property, color]) => {
    if (
      property !== "backgrounds" &&
      property !== "brand_logo" &&
      property !== "logo_hide_title" &&
      property !== "theme_id" &&
      property !== "service_id"
    ) {
      css.setProperty(`--${property}`, formatColorCode(color));
    }
  });
};

export const calculateBounds = (latitudes: number[], longitudes: number[]) => {
  const bounds = [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ];
  return bounds;
};

export const fitBoundsToMap = (
  mapRef: any,
  bounds: number[][],
  options: any = { padding: 100, duration: 500 }
) => {
  if (mapRef?.current) {
    mapRef.current.fitBounds(bounds, options);
  }
};

export const useCopyToClipboard = (text: any) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      console.log("Text copied to clipboard");
    })
    .catch((error) => {
      console.error("Failed to copy text to clipboard: ", error);
    });
};

export const getLocalStorageItem = <T = string,>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item) as T;
    }
    return null;
  } catch (error) {
    console.error(`Error retrieving item from localStorage: ${key}`, error);
    return null;
  }
};

// const css = document.documentElement.style;
// css.setProperty("--primary", formatColorCode(theme.primary));
// css.setProperty("--secondary", formatColorCode(theme.secondary));
// css.setProperty("--primaryLight", formatColorCode(theme.primaryLight));
// css.setProperty("--secondaryDark", formatColorCode(theme.secondaryDark));
// css.setProperty("--background", formatColorCode(theme.background));
// css.setProperty("--tabSecondary", formatColorCode(theme.tabSecondary));
// css.setProperty("--categorySegment",formatColorCode(theme.categorySegment));
// css.setProperty("--success", formatColorCode(theme.success));
// css.setProperty("--error", formatColorCode(theme.error));
